import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, CalendarClock, Wrench, CheckCheck, CalendarOff, Users, ArrowUpRight, HardHat, UserCheck, BellRing, FileWarning, Clock, Truck, PauseCircle, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { DashboardSettings } from "@/components/DashboardSettings";
import { TodaySitesDialog } from "@/components/TodaySitesDialog";
import { useAuth } from "@/lib/auth";

const TILES = [
  ["pending", "Richieste in attesa", Inbox, "/admin/interventi?stato=in_attesa", true],
  ["web_pending", "Nuove richieste dal web da confermare", Globe, "/admin/interventi?stato=in_attesa&web=1", true],
  ["today", "Interventi oggi", CalendarClock, "/admin/mappa"],
  ["in_progress", "In corso", Wrench, "/admin/interventi?stato=in_corso"],
  ["upcoming", "Confermati in arrivo", CheckCheck, "/admin/interventi?stato=confermato"],
  ["absences_pending", "Assenze da valutare", CalendarOff, "/admin/assenze"],
  ["technicians", "Tecnici attivi", Users, "/admin/tecnici"],
  ["cantieri_today", "Cantieri di oggi", HardHat, null],
  ["my_today", "I miei interventi di oggi", UserCheck, "/admin/interventi"],
  ["ack_late", "Tecnici esterni senza risposta", BellRing, "/admin/interventi?stato=confermato"],
  ["external", "Interventi a tecnici esterni", Truck, "/admin/interventi?esterni=1"],
  ["missing_report", "Cantieri senza rapportino", FileWarning, "/admin/cantieri"],
  ["suspended", "Interventi sospesi", PauseCircle, "/admin/interventi?stato=sospeso"],
  ["attendance", "Presenti ora", Clock, "/admin/presenze"],
];

function Tile({ k, label, Icon, hl, value, i }) {
  return (
    <>
      <span className="flex items-center justify-between"><Icon className={`h-4 w-4 ${hl ? "text-[#a2ca6b]" : "text-[#61a229]"}`} /><ArrowUpRight className="h-4 w-4 opacity-40 transition-opacity group-hover:opacity-100" /></span>
      <p className="mt-4 font-display text-3xl font-bold" data-testid={`kpi-${k}-value`}>{value ?? "–"}</p>
      <p className={`text-xs ${hl ? "text-slate-300" : "text-slate-500"}`}>{label}</p>
    </>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState({});
  const [sites, setSites] = useState(false);
  const { user } = useAuth();
  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setData((p) => ({ ...p, ...r.data }))).catch(() => {});
    api.get("/admin/dashboard/summary").then((r) => setData((p) => ({ ...p, ...r.data }))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const tiles = TILES.filter(([k]) => !(user?.dashboard_hidden || []).includes(k));
  const cls = (hl) => `rise group rounded-2xl border p-4 text-left transition-[transform,box-shadow,border-color] hover:-translate-y-0.5 hover:shadow-md ${hl ? "bg-[#0f1710] text-white border-[#0f1710]" : "bg-white hover:border-[#61a229]"}`;
  return (
    <div>
      <PageHeader eyebrow="Centro operativo" title="Dashboard">
        <div className="flex flex-wrap gap-2">
          <DashboardSettings />
          <Button asChild className="rounded-full" data-testid="dashboard-new-job-button"><Link to="/admin/nuovo">Nuovo appuntamento</Link></Button>
        </div>
      </PageHeader>
      {tiles.length ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4" data-testid="dashboard-tiles">
          {tiles.map(([k, label, Icon, to, hl], i) => to
            ? <Link key={k} to={to} className={cls(hl)} style={{ animationDelay: `${i * 0.04}s` }} data-testid={`kpi-${k}`}><Tile k={k} label={label} Icon={Icon} hl={hl} value={data[k]} /></Link>
            : <button key={k} type="button" onClick={() => setSites(true)} className={cls(hl)} style={{ animationDelay: `${i * 0.04}s` }} data-testid={`kpi-${k}`}><Tile k={k} label={label} Icon={Icon} hl={hl} value={data[k]} /></button>)}
        </div>
      ) : <p className="rounded-2xl border bg-white p-10 text-center text-sm text-slate-500" data-testid="dashboard-empty">Nessun riquadro scelto: usa "Personalizza" o il menu.</p>}
      <TodaySitesDialog open={sites} onOpenChange={setSites} />
    </div>
  );
}
